/**
 * Derives Box browsing views from provider entries without mutating remote state.
 *
 * Filtering notes, files, folders, and sort order remains pure so selection and
 * import confirmation operate on the same visible set.
 */
import type { BoxEntry } from './source-api'

export type BoxSort = 'updated-desc' | 'updated-asc' | 'name-asc' | 'name-desc'
export type BoxView = 'files' | 'notes'

export const isBoxNote = (entry: BoxEntry) =>
  entry.type === 'file' && (
    entry.extension?.toLowerCase() === 'boxnote'
    || entry.name.toLowerCase().endsWith('.boxnote')
  )

export const filterAndSortBoxEntries = (
  entries: BoxEntry[],
  query: string,
  sort: BoxSort,
  view: BoxView,
) => {
  const needle = query.trim().toLocaleLowerCase()
  return entries
    .filter(entry => (
      (view === 'files' ? !isBoxNote(entry) : isBoxNote(entry))
    ) && entry.name.toLocaleLowerCase().includes(needle))
    .sort((left, right) => {
      const names = left.name.localeCompare(right.name, undefined, { sensitivity: 'base' })
      if (sort === 'name-asc') return names || left.id.localeCompare(right.id)
      if (sort === 'name-desc') return -names || left.id.localeCompare(right.id)
      const dates = Date.parse(left.modifiedAt ?? '') - Date.parse(right.modifiedAt ?? '')
      if (dates) return sort === 'updated-asc' ? dates : -dates
      return names || left.id.localeCompare(right.id)
    })
}
